// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { certificationApi } from '@/lib/certification-api';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { ICertificateRepository } from './interfaces';

// Certificate entity interfaces
export interface CreateCertificateData {
  userId: string;
  courseId: string;
  certificateTemplateId?: string;
  issuedBy: string;
  issuedAt: Date;
  expiryDate?: Date;
  certificateNumber: string;
  certificateUrl?: string;
  grade?: string;
  scorePercentage?: number;
  completionStatus: 'completed' | 'passed' | 'distinction';
  skillsEarned?: string[];
  notes?: string;
}

export interface UpdateCertificateData {
  certificateTemplateId?: string;
  expiryDate?: Date;
  certificateUrl?: string;
  grade?: string;
  scorePercentage?: number;
  completionStatus?: 'completed' | 'passed' | 'distinction';
  skillsEarned?: string[];
  notes?: string;
  updatedAt?: Timestamp;
}

export interface CertificateFilter {
  userId?: string;
  courseId?: string;
  issuedBy?: string;
  completionStatus?: 'completed' | 'passed' | 'distinction';
  issuedAfter?: Date;
  issuedBefore?: Date;
  expiryAfter?: Date;
  expiryBefore?: Date;
}

export interface GetCertificateData {
  id: string;
  userId: string;
  courseId: string;
  certificateTemplateId?: string;
  issuedBy: string;
  issuedAt: Timestamp;
  expiryDate?: Timestamp;
  certificateNumber: string;
  certificateUrl?: string;
  grade?: string;
  scorePercentage?: number;
  completionStatus: 'completed' | 'passed' | 'distinction';
  skillsEarned?: string[];
  notes?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CertificateTemplateData {
  id: string;
  name: string;
  description?: string;
  templateUrl: string;
  backgroundColor?: string;
  textColor?: string;
  fontFamily?: string;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class CertificateRepository implements ICertificateRepository {
  constructor() {
    console.log('[CertificateRepository] Constructor - Using client-side Firebase DB instance:', !!db);
  }

  private collectionName = 'certificates';
  private templatesCollectionName = 'certificateTemplates';

  private toRepositoryError(
    error: unknown,
    code: string,
    devMessage: string,
    context?: Record<string, unknown>
  ) {
    return toDRXError(error, {
      code,
      devMessage,
      context,
    });
  }

  // Create certificate. Certificates are minted only on the server
  // (functions/src/certificates): the browser cannot write `certificates`.
  // This asks the server to issue one; the caller must be course staff
  // (ADMIN, or INSTRUCTOR who owns the course) and the student enrolled.
  async createCertificate(certificateData: CreateCertificateData): Promise<GetCertificateData> {
    try {
      const templateId =
        (certificateData as CreateCertificateData & { templateId?: string }).templateId ||
        certificateData.certificateTemplateId ||
        null;
      const { certificateId } = await certificationApi.issueCertificate(
        certificateData.courseId,
        certificateData.userId,
        templateId
      );
      const certificate = await this.getCertificateById(certificateId);
      if (!certificate) {
        throw new DRXError({
          code: 'CERTIFICATE_CREATE_MISSING_DOCUMENT',
          devMessage: 'Certificate document was missing after the server issued it',
          context: { certificateId, userId: certificateData.userId, courseId: certificateData.courseId },
        });
      }
      return certificate;
    } catch (error) {
      console.error('[CertificateRepository.createCertificate] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATE_CREATE_FAILED', 'Failed to create certificate', {
        userId: certificateData.userId,
        courseId: certificateData.courseId,
      });
    }
  }

  // Get certificate by ID
  async getCertificateById(certificateId: string): Promise<GetCertificateData | null> {
    console.log('[CertificateRepository.getCertificateById] START - CertificateId:', certificateId);
    try {
      console.log('[CertificateRepository.getCertificateById] Fetching certificate document from Firestore');
      const docRef = doc(db, this.collectionName, certificateId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.log('[CertificateRepository.getCertificateById] Certificate not found');
        return null;
      }

      const certificate = {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetCertificateData;
      console.log('[CertificateRepository.getCertificateById] SUCCESS - Found certificate:', certificate.certificateNumber);
      return certificate;
    } catch (error) {
      console.error('[CertificateRepository.getCertificateById] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATE_FETCH_FAILED', `Failed to get certificate ${certificateId}`, { certificateId });
    }
  }

  // Get certificates with filters
  async getCertificates(filter: CertificateFilter = {}, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getCertificates] START - Filter:', filter, 'LimitCount:', limitCount);
    try {
      console.log('[CertificateRepository.getCertificates] Building Firestore query with filters');
      let q = collection(db, this.collectionName);

      // Build query constraints array
      const constraints = [];

      // Add filters
      if (filter.userId) {
        constraints.push(where('userId', '==', filter.userId));
      }
      if (filter.courseId) {
        constraints.push(where('courseId', '==', filter.courseId));
      }
      if (filter.issuedBy) {
        constraints.push(where('issuedBy', '==', filter.issuedBy));
      }
      if (filter.completionStatus) {
        constraints.push(where('completionStatus', '==', filter.completionStatus));
      }

      // Add limit if specified
      if (limitCount) {
        constraints.push(limit(limitCount));
      }

      // Build the final query
      if (constraints.length > 0) {
        q = query(q, ...constraints);
        console.log('[CertificateRepository.getCertificates] Built query with', constraints.length, 'constraints');
      } else {
        console.log('[CertificateRepository.getCertificates] No constraints, using simple collection query');
      }

      const querySnapshot = await getDocs(q);
      let certificates: GetCertificateData[] = [];

      querySnapshot.forEach((doc) => {
        certificates.push({
          id: doc.id,
          ...doc.data(),
        } as GetCertificateData);
      });

      // Apply date filters (since Firestore doesn't support range queries with other filters easily)
      if (filter.issuedAfter) {
        certificates = certificates.filter(cert =>
          cert.issuedAt.toDate() >= filter.issuedAfter!
        );
      }
      if (filter.issuedBefore) {
        certificates = certificates.filter(cert =>
          cert.issuedAt.toDate() <= filter.issuedBefore!
        );
      }
      if (filter.expiryAfter) {
        certificates = certificates.filter(cert =>
          cert.expiryDate && cert.expiryDate.toDate() >= filter.expiryAfter!
        );
      }
      if (filter.expiryBefore) {
        certificates = certificates.filter(cert =>
          cert.expiryDate && cert.expiryDate.toDate() <= filter.expiryBefore!
        );
      }

      console.log('[CertificateRepository.getCertificates] SUCCESS - Retrieved', certificates.length, 'certificates');
      return certificates;
    } catch (error) {
      console.error('[CertificateRepository.getCertificates] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_FETCH_FAILED', 'Failed to get certificates', { filter, limitCount });
    }
  }

  // Update certificate. Issued certificates are immutable from the browser
  // (firestore.rules); the only legitimate change is revocation.
  async updateCertificate(certificateId: string, updateData: UpdateCertificateData): Promise<GetCertificateData> {
    throw new DRXError({
      code: 'CERTIFICATE_UPDATE_NOT_ALLOWED',
      devMessage: `Certificates cannot be edited from the client (certificate ${certificateId})`,
      userMessage: 'Issued certificates cannot be edited. Revoke and re-issue instead.',
      level: 'warning',
      context: { certificateId, updateData },
    });
  }

  // "Delete" certificate. A deleted certificate would make its verification
  // link say "not found" instead of "revoked", so this revokes it on the server.
  async deleteCertificate(certificateId: string): Promise<void> {
    await this.revokeCertificate(certificateId);
  }

  // Get certificate by number
  async getCertificateByNumber(certificateNumber: string): Promise<GetCertificateData | null> {
    console.log('[CertificateRepository.getCertificateByNumber] START - CertificateNumber:', certificateNumber);
    try {
      console.log('[CertificateRepository.getCertificateByNumber] Querying certificate by number');
      const q = query(
        collection(db, this.collectionName),
        where('certificateNumber', '==', certificateNumber)
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        console.log('[CertificateRepository.getCertificateByNumber] Certificate not found with number:', certificateNumber);
        return null;
      }

      const doc = querySnapshot.docs[0];
      const certificate = {
        id: doc.id,
        ...doc.data(),
      } as GetCertificateData;
      console.log('[CertificateRepository.getCertificateByNumber] SUCCESS - Found certificate with ID:', certificate.id);
      return certificate;
    } catch (error) {
      console.error('[CertificateRepository.getCertificateByNumber] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATE_BY_NUMBER_FETCH_FAILED', `Failed to get certificate by number ${certificateNumber}`, { certificateNumber });
    }
  }

  // Get certificates by issued by
  async getCertificatesByIssuer(issuedBy: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getCertificatesByIssuer] START - IssuedBy:', issuedBy, 'LimitCount:', limitCount);
    try {
      console.log('[CertificateRepository.getCertificatesByIssuer] Filtering certificates by issuer');
      const filter: CertificateFilter = { issuedBy: issuedBy };
      const result = await this.getCertificates(filter, limitCount);
      console.log('[CertificateRepository.getCertificatesByIssuer] SUCCESS - Retrieved', result.length, 'certificates for issuer');
      return result;
    } catch (error) {
      console.error('[CertificateRepository.getCertificatesByIssuer] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_BY_ISSUER_FETCH_FAILED', `Failed to get certificates for issuer ${issuedBy}`, { issuedBy, limitCount });
    }
  }

  // Get certificates by status
  async getCertificatesByStatus(completionStatus: 'completed' | 'passed' | 'distinction', limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getCertificatesByStatus] START - CompletionStatus:', completionStatus, 'LimitCount:', limitCount);
    try {
      console.log('[CertificateRepository.getCertificatesByStatus] Filtering certificates by status');
      const filter: CertificateFilter = { completionStatus: completionStatus };
      const result = await this.getCertificates(filter, limitCount);
      console.log('[CertificateRepository.getCertificatesByStatus] SUCCESS - Retrieved', result.length, 'certificates with status');
      return result;
    } catch (error) {
      console.error('[CertificateRepository.getCertificatesByStatus] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_BY_STATUS_FETCH_FAILED', `Failed to get certificates with status ${completionStatus}`, { completionStatus, limitCount });
    }
  }

  // Get certificates by owner (issuedBy)
  async getCertificatesByOwner(ownerId: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getCertificatesByOwner] START - OwnerId:', ownerId, 'LimitCount:', limitCount);
    try {
      console.log('[CertificateRepository.getCertificatesByOwner] Delegating to getCertificatesByIssuer');
      const filter: CertificateFilter = { issuedBy: ownerId };
      const result = await this.getCertificates(filter, limitCount);
      console.log('[CertificateRepository.getCertificatesByOwner] SUCCESS - Retrieved', result.length, 'certificates for owner');
      return result;
    } catch (error) {
      console.error('[CertificateRepository.getCertificatesByOwner] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_BY_OWNER_FETCH_FAILED', `Failed to get certificates for owner ${ownerId}`, { ownerId, limitCount });
    }
  }

  // Get all certificates (admin only) or owner's certificates
  async getCertificatesForUser(userId: string, isAdmin: boolean = false, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getCertificatesForUser] START - UserId:', userId, 'IsAdmin:', isAdmin, 'LimitCount:', limitCount);
    try {
      console.log('[CertificateRepository.getCertificatesForUser] Determining access level and retrieving certificates');
      let result: GetCertificateData[];
      if (isAdmin) {
        // Admin sees all certificates
        console.log('[CertificateRepository.getCertificatesForUser] Admin access - retrieving all certificates');
        result = await this.getCertificates({}, limitCount);
      } else {
        // Regular user sees only certificates they issued
        console.log('[CertificateRepository.getCertificatesForUser] Regular user access - retrieving issued certificates');
        result = await this.getCertificatesByOwner(userId, limitCount);
      }
      console.log('[CertificateRepository.getCertificatesForUser] SUCCESS - Retrieved', result.length, 'certificates for user');
      return result;
    } catch (error) {
      console.error('[CertificateRepository.getCertificatesForUser] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_FOR_USER_FETCH_FAILED', `Failed to get certificates for user ${userId}`, { userId, isAdmin, limitCount });
    }
  }

  // Get all certificates (admin only)
  async getAllCertificates(limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getAllCertificates] START - LimitCount:', limitCount);
    try {
      console.log('[CertificateRepository.getAllCertificates] Retrieving all certificates (admin access)');
      const result = await this.getCertificates({}, limitCount);
      console.log('[CertificateRepository.getAllCertificates] SUCCESS - Retrieved', result.length, 'certificates');
      return result;
    } catch (error) {
      console.error('[CertificateRepository.getAllCertificates] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_ALL_FETCH_FAILED', 'Failed to get all certificates', { limitCount });
    }
  }

  // Upload certificate template background image
  async uploadCertificateTemplate(templateId: string, file: File): Promise<string> {
    console.log('[CertificateRepository.uploadCertificateTemplate] START - TemplateId:', templateId, 'FileName:', file.name);
    try {
      console.log('[CertificateRepository.uploadCertificateTemplate] Simulating template upload process');

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate a mock URL
      const templateUrl = `https://storage.example.com/certificates/templates/${templateId}/template_${Date.now()}_${file.name}`;

      // Update template with new image URL
      const templatesRef = collection(db, this.templatesCollectionName);
      await updateDoc(doc(templatesRef, templateId), {
        templateUrl: templateUrl,
        updatedAt: serverTimestamp(),
      });

      console.log('[CertificateRepository.uploadCertificateTemplate] SUCCESS - Uploaded template:', templateUrl);
      return templateUrl;
    } catch (error) {
      console.error('[CertificateRepository.uploadCertificateTemplate] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATE_TEMPLATE_UPLOAD_FAILED', `Failed to upload certificate template ${templateId}`, {
        templateId,
        fileName: file.name,
      });
    }
  }

  /**
   * Fetch a signed URL for the certificate's rendered PDF.
   *
   * This used to be a stub: it slept two seconds and returned a
   * `storage.example.com` URL that never existed, so anything relying on it
   * got a dead link and no error. Rendering is now server-side
   * (functions/src/certificates/render.ts); this asks for a short-lived
   * signed URL, and the server draws the PDF on demand if it is missing.
   */
  async generateCertificateImage(certificateId: string, _templateId?: string): Promise<string> {
    try {
      const mint = httpsCallable<{ certificateId: string }, { url: string }>(
        functions,
        'mintCertificateDownloadUrl'
      );
      const res = await mint({ certificateId });
      if (!res.data?.url) {
        throw new DRXError({
          code: 'CERTIFICATE_IMAGE_NO_URL',
          devMessage: 'mintCertificateDownloadUrl returned no url',
          context: { certificateId },
        });
      }
      return res.data.url;
    } catch (error) {
      throw this.toRepositoryError(
        error,
        'CERTIFICATE_IMAGE_GENERATE_FAILED',
        `Failed to get certificate PDF for ${certificateId}`,
        { certificateId }
      );
    }
  }

  // Delete certificate template
  async deleteCertificateTemplate(templateId: string): Promise<void> {
    console.log('[CertificateRepository.deleteCertificateTemplate] START - TemplateId:', templateId);
    try {
      console.log('[CertificateRepository.deleteCertificateTemplate] Deleting template from storage and updating document');
      // In real implementation, delete from cloud storage
      console.log('[CertificateRepository.deleteCertificateTemplate] Simulating template deletion from storage');

      // Update template to remove URL
      const templatesRef = collection(db, this.templatesCollectionName);
      await updateDoc(doc(templatesRef, templateId), {
        templateUrl: null,
        updatedAt: serverTimestamp(),
      });
      console.log('[CertificateRepository.deleteCertificateTemplate] SUCCESS - Deleted certificate template');
    } catch (error) {
      console.error('[CertificateRepository.deleteCertificateTemplate] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATE_TEMPLATE_DELETE_FAILED', `Failed to delete certificate template ${templateId}`, { templateId });
    }
  }

  // Get expired notifications
  async getExpiredNotifications(): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getExpiredNotifications] START - Getting expired certificates');
    try {
      console.log('[CertificateRepository.getExpiredNotifications] Querying certificates with expiry dates in the past');
      const now = Timestamp.fromDate(new Date());
      const q = query(
        collection(db, this.collectionName),
        where('expiryDate', '<=', now),
        orderBy('expiryDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const certificates: GetCertificateData[] = [];

      querySnapshot.forEach((doc) => {
        certificates.push({
          id: doc.id,
          ...doc.data(),
        } as GetCertificateData);
      });

      console.log('[CertificateRepository.getExpiredNotifications] SUCCESS - Retrieved', certificates.length, 'expired certificates');
      return certificates;
    } catch (error) {
      console.error('[CertificateRepository.getExpiredNotifications] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_EXPIRED_FETCH_FAILED', 'Failed to get expired certificates');
    }
  }

  // Clean up expired notifications
  async cleanupExpiredNotifications(): Promise<number> {
    console.log('[CertificateRepository.cleanupExpiredNotifications] START - Cleaning up expired certificates');
    try {
      console.log('[CertificateRepository.cleanupExpiredNotifications] Getting expired certificates for deletion');
      const expiredCertificates = await this.getExpiredNotifications();

      console.log('[CertificateRepository.cleanupExpiredNotifications] Deleting', expiredCertificates.length, 'expired certificates');
      const deletePromises = expiredCertificates.map(certificate =>
        deleteDoc(doc(db, this.collectionName, certificate.id))
      );
      await Promise.all(deletePromises);
      console.log('[CertificateRepository.cleanupExpiredNotifications] SUCCESS - Cleaned up', expiredCertificates.length, 'expired certificates');
      return expiredCertificates.length;
    } catch (error) {
      console.error('[CertificateRepository.cleanupExpiredNotifications] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_EXPIRED_CLEANUP_FAILED', 'Failed to clean up expired certificates');
    }
  }

  // Get user certificates
  async getUserCertificates(userId: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getUserCertificates] START - UserId:', userId, 'LimitCount:', limitCount);
    try {
      console.log('[CertificateRepository.getUserCertificates] Filtering certificates by user ID');
      const filter: CertificateFilter = { userId: userId };
      const result = await this.getCertificates(filter, limitCount);
      console.log('[CertificateRepository.getUserCertificates] SUCCESS - Retrieved', result.length, 'certificates for user');
      return result;
    } catch (error) {
      console.error('[CertificateRepository.getUserCertificates] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_BY_USER_FETCH_FAILED', `Failed to get certificates for user ${userId}`, { userId, limitCount });
    }
  }

  // Get course certificates
  async getCourseCertificates(courseId: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getCourseCertificates] START - CourseId:', courseId, 'LimitCount:', limitCount);
    try {
      console.log('[CertificateRepository.getCourseCertificates] Filtering certificates by course ID');
      // Course staff may read every certificate of a course; its instructor
      // only those naming them (rules: instructorId == uid). A query by course
      // alone cannot prove that, so for the instructor it was refused and the
      // course editor showed no certificates and no eligible students. Every
      // certificate of a course carries that course's instructorId, so the
      // owner's narrower query returns the same set.
      const uid = auth.currentUser?.uid;
      const course = uid ? await getDoc(doc(db, 'certificationCourses', courseId)) : null;
      let result: GetCertificateData[];
      if (uid && course?.exists() && course.data().instructorId === uid) {
        const mine = await getDocs(
          query(collection(db, this.collectionName), where('courseId', '==', courseId), where('instructorId', '==', uid)),
        );
        result = mine.docs.map((d) => ({ id: d.id, ...d.data() }) as unknown as GetCertificateData);
      } else {
        result = await this.getCertificates({ courseId } as CertificateFilter, limitCount);
      }
      console.log('[CertificateRepository.getCourseCertificates] SUCCESS - Retrieved', result.length, 'certificates for course');
      return result;
    } catch (error) {
      console.error('[CertificateRepository.getCourseCertificates] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_BY_COURSE_FETCH_FAILED', `Failed to get certificates for course ${courseId}`, { courseId, limitCount });
    }
  }

  // Verify certificate by its public code (works signed out). Returns only the
  // fields a certificate itself shows; null when the code is unknown.
  async verifyCertificate(certificateNumber: string): Promise<GetCertificateData | null> {
    try {
      const v = await certificationApi.verifyCertificate(certificateNumber);
      if (!v.found) return null;
      return {
        id: v.verificationCode || certificateNumber,
        userId: '',
        courseId: '',
        issuedBy: v.instructorName || 'Dance RealmX',
        issuedAt: v.issuedAt,
        certificateNumber: v.verificationCode || certificateNumber,
        completionStatus: 'completed',
        createdAt: v.issuedAt,
        updatedAt: v.revokedAt || v.issuedAt,
        ...(v as object),
      } as unknown as GetCertificateData;
    } catch (error) {
      throw this.toRepositoryError(error, 'CERTIFICATE_VERIFY_FAILED', `Failed to verify certificate ${certificateNumber}`, { certificateNumber });
    }
  }

  // Get expiring certificates
  async getExpiringCertificates(daysAhead: number = 30): Promise<GetCertificateData[]> {
    console.log('[CertificateRepository.getExpiringCertificates] START - DaysAhead:', daysAhead);
    try {
      console.log('[CertificateRepository.getExpiringCertificates] Querying certificates expiring within', daysAhead, 'days');
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + daysAhead);

      const q = query(
        collection(db, this.collectionName),
        where('expiryDate', '<=', Timestamp.fromDate(futureDate)),
        where('expiryDate', '>', Timestamp.fromDate(new Date())),
        orderBy('expiryDate', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const certificates: GetCertificateData[] = [];

      querySnapshot.forEach((doc) => {
        certificates.push({
          id: doc.id,
          ...doc.data(),
        } as GetCertificateData);
      });

      console.log('[CertificateRepository.getExpiringCertificates] SUCCESS - Retrieved', certificates.length, 'expiring certificates');
      return certificates;
    } catch (error) {
      console.error('[CertificateRepository.getExpiringCertificates] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATES_EXPIRING_FETCH_FAILED', 'Failed to get expiring certificates', { daysAhead });
    }
  }

  // Create certificate template
  async createCertificateTemplate(templateData: Omit<CertificateTemplateData, 'id' | 'createdAt' | 'updatedAt'>): Promise<CertificateTemplateData> {
    console.log('[CertificateRepository.createCertificateTemplate] START - TemplateData:', templateData);
    try {
      console.log('[CertificateRepository.createCertificateTemplate] Creating certificate template document');
      const docRef = await addDoc(collection(db, this.templatesCollectionName), {
        ...templateData,
        isActive: templateData.isActive ?? true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'CERTIFICATE_TEMPLATE_CREATE_MISSING_DOCUMENT',
          devMessage: 'Certificate template document was missing after create',
          context: { name: templateData.name },
        });
      }

      const template = {
        id: docRef.id,
        ...docSnap.data(),
      } as CertificateTemplateData;
      console.log('[CertificateRepository.createCertificateTemplate] SUCCESS - Created template with ID:', template.id, 'Name:', template.name);
      return template;
    } catch (error) {
      console.error('[CertificateRepository.createCertificateTemplate] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATE_TEMPLATE_CREATE_FAILED', 'Failed to create certificate template', {
        name: templateData.name,
        isActive: templateData.isActive,
      });
    }
  }

  // Get certificate templates
  async getCertificateTemplates(activeOnly: boolean = false): Promise<CertificateTemplateData[]> {
    console.log('[CertificateRepository.getCertificateTemplates] START - ActiveOnly:', activeOnly);
    try {
      console.log('[CertificateRepository.getCertificateTemplates] Querying certificate templates');
      let q = query(collection(db, this.templatesCollectionName), orderBy('createdAt', 'desc'));

      if (activeOnly) {
        q = query(q, where('isActive', '==', true));
      }

      const querySnapshot = await getDocs(q);
      const templates: CertificateTemplateData[] = [];

      querySnapshot.forEach((doc) => {
        templates.push({
          id: doc.id,
          ...doc.data(),
        } as CertificateTemplateData);
      });

      console.log('[CertificateRepository.getCertificateTemplates] SUCCESS - Retrieved', templates.length, 'templates');
      return templates;
    } catch (error) {
      console.error('[CertificateRepository.getCertificateTemplates] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATE_TEMPLATES_FETCH_FAILED', 'Failed to get certificate templates', { activeOnly });
    }
  }

  // Generate certificate PDF (placeholder - would integrate with PDF generation service)
  async generateCertificatePDF(certificateId: string): Promise<string> {
    console.log('[CertificateRepository.generateCertificatePDF] START - CertificateId:', certificateId);
    try {
      console.log('[CertificateRepository.generateCertificatePDF] Generating PDF for certificate (placeholder implementation)');
      // This would integrate with a PDF generation service
      // For now, return a placeholder URL
      const pdfUrl = `https://example.com/certificates/${certificateId}.pdf`;
      console.log('[CertificateRepository.generateCertificatePDF] SUCCESS - Generated PDF URL:', pdfUrl);
      return pdfUrl;
    } catch (error) {
      console.error('[CertificateRepository.generateCertificatePDF] ERROR:', error);
      throw this.toRepositoryError(error, 'CERTIFICATE_PDF_GENERATE_FAILED', `Failed to generate certificate PDF for ${certificateId}`, { certificateId });
    }
  }

  // Revoke certificate (course staff), on the server.
  async revokeCertificate(certificateId: string, reason?: string): Promise<{ id: string; status: string }> {
    try {
      await certificationApi.revokeCertificate(certificateId, reason || 'Revoked by instructor');
      return { id: certificateId, status: 'revoked' };
    } catch (error) {
      throw new DRXError({
        code: 'CERTIFICATE_REVOKE_FAILED',
        devMessage: `Failed to revoke certificate ${certificateId}`,
        userMessage: (error as { message?: string })?.message || 'Failed to revoke certificate.',
        cause: error,
      });
    }
  }
}
